import React, { useState, useEffect } from 'react';
import Navigation from '@/components/shared/Navigation';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { 
  Bell, 
  Mail, 
  MessageSquare, 
  Moon, 
  Sun, 
  Globe, 
  Clock,
  Save,
  RefreshCw,
  Smartphone,
  Volume2,
  VolumeX
} from 'lucide-react';
import { userSettingsApi } from '@/lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';

interface UserPreferences {
  notifications_enabled: boolean;
  email_notifications: boolean;
  sms_notifications: boolean;
  push_notifications: boolean;
  sound_notifications: boolean;
  language: string;
  timezone: string;
  theme: string;
  auto_renew_reminders: boolean;
  payment_reminders: boolean;
  event_notifications: boolean;
  newsletter_subscription: boolean;
  marketing_emails: boolean;
  weekly_digest: boolean;
  mobile_notifications: boolean;
}

const UserPreferences: React.FC = () => {
  const [preferences, setPreferences] = useState<UserPreferences>({
    notifications_enabled: true,
    email_notifications: true,
    sms_notifications: false,
    push_notifications: true,
    sound_notifications: true,
    language: 'en',
    timezone: 'Africa/Dar_es_Salaam',
    theme: 'light',
    auto_renew_reminders: true,
    payment_reminders: true,
    event_notifications: true,
    newsletter_subscription: false,
    marketing_emails: false,
    weekly_digest: true,
    mobile_notifications: true,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'sw', label: 'Kiswahili' },
    { value: 'fr', label: 'Français' },
    { value: 'ar', label: 'العربية' },
  ];

  const timezones = [
    { value: 'Africa/Dar_es_Salaam', label: 'East Africa Time (EAT)' },
    { value: 'Africa/Nairobi', label: 'East Africa Time (Kenya)' },
    { value: 'Africa/Kampala', label: 'East Africa Time (Uganda)' },
    { value: 'UTC', label: 'UTC' },
    { value: 'Europe/London', label: 'GMT' },
    { value: 'America/New_York', label: 'Eastern Time' },
  ];

  const themes = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'auto', label: 'Auto', icon: Globe },
  ];

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setIsLoading(true);
      const data = await userSettingsApi.getPreferences();
      setPreferences(prev => ({ ...prev, ...data }));
      setHasChanges(false);
    } catch (error: any) {
      console.error('Error fetching preferences:', error);
      toast({
        title: "Error",
        description: "Failed to load preferences. Using default settings.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreferenceChange = (key: keyof UserPreferences, value: any) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await userSettingsApi.updatePreferences(preferences);
      setHasChanges(false);
      toast({
        title: "Success",
        description: "Preferences saved successfully",
      });
    } catch (error: any) {
      console.error('Error saving preferences:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save preferences",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    fetchPreferences();
    setHasChanges(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center min-h-[50vh]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">User Preferences</h1>
          <p className="text-gray-600">Customize your experience and notification settings</p>
        </div>

        <div className="space-y-6">
          {/* Notification Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notification Preferences
              </CardTitle>
              <CardDescription>
                Control how and when you receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Master Notifications Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base font-medium">Enable Notifications</Label>
                  <p className="text-sm text-gray-500">
                    Master control for all notifications
                  </p>
                </div>
                <Switch
                  checked={preferences.notifications_enabled}
                  onCheckedChange={(checked) => handlePreferenceChange('notifications_enabled', checked)}
                />
              </div>

              <Separator />

              {/* Email Notifications */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5 flex items-center gap-3">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <div>
                    <Label className="text-base font-medium">Email Notifications</Label>
                    <p className="text-sm text-gray-500">
                      Receive notifications via email
                    </p>
                  </div>
                </div>
                <Switch
                  checked={preferences.email_notifications}
                  onCheckedChange={(checked) => handlePreferenceChange('email_notifications', checked)}
                  disabled={!preferences.notifications_enabled}
                />
              </div>

              {/* SMS Notifications */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5 flex items-center gap-3">
                  <MessageSquare className="w-4 h-4 text-gray-500" />
                  <div>
                    <Label className="text-base font-medium">SMS Notifications</Label>
                    <p className="text-sm text-gray-500">
                      Receive notifications via SMS
                    </p>
                  </div>
                </div>
                <Switch
                  checked={preferences.sms_notifications}
                  onCheckedChange={(checked) => handlePreferenceChange('sms_notifications', checked)}
                  disabled={!preferences.notifications_enabled}
                />
              </div>

              {/* Push Notifications */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5 flex items-center gap-3">
                  <Smartphone className="w-4 h-4 text-gray-500" />
                  <div>
                    <Label className="text-base font-medium">Push Notifications</Label>
                    <p className="text-sm text-gray-500">
                      Receive push notifications in your browser
                    </p>
                  </div>
                </div>
                <Switch
                  checked={preferences.push_notifications}
                  onCheckedChange={(checked) => handlePreferenceChange('push_notifications', checked)}
                  disabled={!preferences.notifications_enabled}
                />
              </div>

              {/* Sound Notifications */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5 flex items-center gap-3">
                  {preferences.sound_notifications ? (
                    <Volume2 className="w-4 h-4 text-gray-500" />
                  ) : (
                    <VolumeX className="w-4 h-4 text-gray-500" />
                  )}
                  <div>
                    <Label className="text-base font-medium">Sound Notifications</Label>
                    <p className="text-sm text-gray-500">
                      Play sounds for notifications
                    </p>
                  </div>
                </div>
                <Switch
                  checked={preferences.sound_notifications}
                  onCheckedChange={(checked) => handlePreferenceChange('sound_notifications', checked)}
                  disabled={!preferences.notifications_enabled}
                />
              </div>
            </CardContent>
          </Card>

          {/* Specific Notification Types */}
          <Card>
            <CardHeader>
              <CardTitle>Notification Types</CardTitle>
              <CardDescription>
                Choose which types of notifications you want to receive
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Payment Reminders</Label>
                    <p className="text-xs text-gray-500">Monthly payment due dates</p>
                  </div>
                  <Switch
                    checked={preferences.payment_reminders}
                    onCheckedChange={(checked) => handlePreferenceChange('payment_reminders', checked)}
                    disabled={!preferences.notifications_enabled}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Auto-Renew Reminders</Label>
                    <p className="text-xs text-gray-500">Membership renewal notifications</p>
                  </div>
                  <Switch
                    checked={preferences.auto_renew_reminders}
                    onCheckedChange={(checked) => handlePreferenceChange('auto_renew_reminders', checked)}
                    disabled={!preferences.notifications_enabled}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Event Notifications</Label>
                    <p className="text-xs text-gray-500">Club events and activities</p>
                  </div>
                  <Switch
                    checked={preferences.event_notifications}
                    onCheckedChange={(checked) => handlePreferenceChange('event_notifications', checked)}
                    disabled={!preferences.notifications_enabled}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Weekly Digest</Label>
                    <p className="text-xs text-gray-500">Weekly summary emails</p>
                  </div>
                  <Switch
                    checked={preferences.weekly_digest}
                    onCheckedChange={(checked) => handlePreferenceChange('weekly_digest', checked)}
                    disabled={!preferences.notifications_enabled}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Newsletter</Label>
                    <p className="text-xs text-gray-500">Monthly club newsletter</p>
                  </div>
                  <Switch
                    checked={preferences.newsletter_subscription}
                    onCheckedChange={(checked) => handlePreferenceChange('newsletter_subscription', checked)}
                    disabled={!preferences.notifications_enabled}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Marketing Emails</Label>
                    <p className="text-xs text-gray-500">Promotional offers and updates</p>
                  </div>
                  <Switch
                    checked={preferences.marketing_emails}
                    onCheckedChange={(checked) => handlePreferenceChange('marketing_emails', checked)}
                    disabled={!preferences.notifications_enabled}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Display & Localization */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Display & Localization
              </CardTitle>
              <CardDescription>
                Customize your display preferences and regional settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Theme Selection */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Theme</Label>
                <div className="grid grid-cols-3 gap-3">
                  {themes.map((theme) => {
                    const IconComponent = theme.icon;
                    return (
                      <button
                        key={theme.value}
                        onClick={() => handlePreferenceChange('theme', theme.value)}
                        className={`p-3 rounded-lg border-2 transition-colors flex flex-col items-center gap-2 ${
                          preferences.theme === theme.value
                            ? 'border-basketball-orange-500 bg-basketball-orange-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <IconComponent className="w-5 h-5" />
                        <span className="text-sm font-medium">{theme.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Separator />

              {/* Language Selection */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Language</Label>
                <Select
                  value={preferences.language}
                  onValueChange={(value) => handlePreferenceChange('language', value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((lang) => (
                      <SelectItem key={lang.value} value={lang.value}>
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Timezone Selection */}
              <div className="space-y-3">
                <Label className="text-base font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Timezone
                </Label>
                <Select
                  value={preferences.timezone}
                  onValueChange={(value) => handlePreferenceChange('timezone', value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timezones.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Save Actions */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {hasChanges && "You have unsaved changes"}
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!hasChanges || isSaving}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              
              <Button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
              >
                {isSaving ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Preferences
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPreferences; 